import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface FamilyMemberCheckin {
  id: string;
  client_id: string;
  member_type: 'partner' | 'child';
  member_name: string;
  member_age?: number;
  status: 'checked-in' | 'checked-out';
  check_in_time?: string;
  check_out_time?: string;
  checked_in_by?: string;
  checked_out_by?: string;
  site_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const useFamilyMemberCheckins = (clientId?: string, siteId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: memberCheckins = [], isLoading, refetch } = useQuery({
    queryKey: ['family-member-checkins', clientId, siteId],
    queryFn: async () => {
      let query = supabase
        .from('family_member_checkins')
        .select('*')
        .order('member_type', { ascending: true })
        .order('member_name', { ascending: true });

      if (clientId) {
        query = query.eq('client_id', clientId);
      }
      if (siteId) {
        query = query.eq('site_id', siteId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FamilyMemberCheckin[];
    },
    enabled: !!clientId || !!siteId,
  });

  const toggleMemberStatus = useMutation({
    mutationFn: async ({ 
      memberId, 
      newStatus,
      userId,
      memberName,
      memberType,
      clientId,
      siteId,
    }: { 
      memberId: string; 
      newStatus: 'checked-in' | 'checked-out';
      userId: string;
      memberName: string;
      memberType: string;
      clientId: string;
      siteId?: string;
    }) => {
      const now = new Date().toISOString();
      const updates: Partial<FamilyMemberCheckin> = {
        status: newStatus,
        updated_at: now,
      };

      if (newStatus === 'checked-in') {
        updates.check_in_time = now;
        updates.checked_in_by = userId;
      } else {
        updates.check_out_time = now;
        updates.checked_out_by = userId;
      }

      const { error } = await supabase
        .from('family_member_checkins')
        .update(updates)
        .eq('id', memberId);

      if (error) throw error;

      // Log activity for this family member check-in/out
      await supabase.from('user_activity_logs').insert({
        user_id: userId,
        action_type: newStatus === 'checked-in' ? 'family_member_check_in' : 'family_member_check_out',
        action_details: {
          member_name: memberName,
          member_type: memberType,
          client_id: clientId,
          timestamp: now,
        },
        site_id: siteId,
      });

      return { memberId, newStatus, timestamp: now, memberName };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['family-member-checkins'] });
      toast({
        title: `${result.memberName} ${result.newStatus === 'checked-in' ? 'checked in' : 'checked out'}`,
        description: `Status updated at ${new Date(result.timestamp).toLocaleTimeString()}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error updating status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const initializeFamilyMembers = useMutation({
    mutationFn: async ({
      clientId,
      siteId,
      partnerName,
      children,
    }: {
      clientId: string;
      siteId: string;
      partnerName?: string;
      children?: { name: string; age?: number }[];
    }) => {
      const members: {
        client_id: string;
        site_id: string;
        member_type: string;
        member_name: string;
        member_age?: number;
        status: string;
      }[] = [];

      if (partnerName) {
        members.push({
          client_id: clientId,
          site_id: siteId,
          member_type: 'partner',
          member_name: partnerName,
          status: 'checked-out',
        });
      }

      if (children && children.length > 0) {
        children.forEach((child) => {
          members.push({
            client_id: clientId,
            site_id: siteId,
            member_type: 'child',
            member_name: child.name,
            member_age: child.age,
            status: 'checked-out',
          });
        });
      }

      if (members.length === 0) return [];

      const { data, error } = await supabase
        .from('family_member_checkins')
        .insert(members)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-member-checkins'] });
    },
  });

  const checkInAllMembers = useMutation({
    mutationFn: async ({ clientId, userId }: { clientId: string; userId: string }) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('family_member_checkins')
        .update({
          status: 'checked-in',
          check_in_time: now,
          checked_in_by: userId,
          updated_at: now,
        })
        .eq('client_id', clientId);

      if (error) throw error;
      return now;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-member-checkins'] });
      toast({ title: 'All family members checked in' });
    },
  });

  const checkOutAllMembers = useMutation({
    mutationFn: async ({ clientId, userId }: { clientId: string; userId: string }) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('family_member_checkins')
        .update({
          status: 'checked-out',
          check_out_time: now,
          checked_out_by: userId,
          updated_at: now,
        })
        .eq('client_id', clientId);

      if (error) throw error;
      return now;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-member-checkins'] });
      toast({ title: 'All family members checked out' });
    },
  });

  // Calculate family status summary
  const getFamilyStatus = (clientMemberCheckins: FamilyMemberCheckin[]) => {
    if (clientMemberCheckins.length === 0) return null;
    
    const checkedIn = clientMemberCheckins.filter(m => m.status === 'checked-in').length;
    const total = clientMemberCheckins.length;
    
    if (checkedIn === total) return 'all-in';
    if (checkedIn === 0) return 'all-out';
    return 'partial';
  };

  return {
    memberCheckins,
    isLoading,
    refetch,
    toggleMemberStatus,
    initializeFamilyMembers,
    checkInAllMembers,
    checkOutAllMembers,
    getFamilyStatus,
  };
};
